/**
 * Byt hyresgäst — egen sida.
 *
 * Bytet gick förut genom den vanliga ansökningsguiden med en anteckning i
 * sessionStorage. Guiden visste inget om den gamla uthyrningen, så perioden
 * den visade och validerade mot var standardperioden för en ny ansökan
 * (i dag → ett år framåt). Saknades anteckningen föll guiden tyst tillbaka
 * till sina vanliga vägar.
 *
 * Här är sanningen databasen: sidan läser den gamla ansökan, den valda
 * perioden ligger i adressen (överlever omladdning och ny flik) och
 * kontrolleras mot den godkända uthyrningen innan formuläret visas. Samma
 * kontroll körs igen i changeTenant på servern.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../../config/firebase';
import { CreateStep2 } from '../../components/createSteps/createStep2';
import { ApplicationSentModal } from '../../components/applicationSentModal';
import { Button } from '../../components/button';
import { useAPI } from '../../hooks/useAPI';
import { COLORS } from '../../theme';
import { formatPersonalNumber } from '../../utils/formatting';
import { formatDateSv } from '../../utils/dates';
import {
  approvedRentalPeriod,
  validateContractWithinPeriod,
  validateTenantChangePeriod,
} from '../../utils/tenantChange';

type Period = { start: string; end: string };

type PageState =
  | { kind: 'loading' }
  | { kind: 'blocked'; message: string }
  | { kind: 'ready'; period: Period; approvedEnd: string; application: Record<string, any> };

const emptyValues = (application: Record<string, any>, period: Period) => ({
  fromDate: dayjs(period.start),
  toDate: dayjs(period.end),
  selectedAssociation: '',
  selectedApartment: '',
  address: application.address || '',
  zipCode: application.zipCode || application.postalCode || '',
  city: application.city || '',
  reason: application.reason || '',
  apartmentNumber: application.apartmentNumber || '',
  tenantFirstName: '',
  tenantLastName: '',
  tenantPhoneNumber: '',
  tenantEmail: '',
  tenantEmailConfirm: '',
  tenantPersonalNumber: '',
  squareMeter: application.squareMeter ?? null,
  rooms: application.rooms ?? null,
  contractStartDate: dayjs(period.start),
  contractEndDate: dayjs(period.end),
  rentalFee: null,
  deposit: null,
  clearingnummer: '',
  bankAccountNumber: '',
  bankgiro: '',
  heatingIncluded: null,
  hotWaterIncluded: null,
  electricityIncluded: null,
  internetIncluded: null,
  parkingIncluded: null,
  furnitureIncluded: null,
  miscellaneousNotes: '',
});

const ChangeTenant: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { changeTenant, isLoading } = useAPI();

  const [page, setPage] = useState<PageState>({ kind: 'loading' });
  const [values, setValues] = useState<Record<string, any>>({});
  const [step2HasErrors, setStep2HasErrors] = useState(true);
  const [triggerStep2Validation, setTriggerStep2Validation] = useState<(() => boolean) | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!id) {
      setPage({ kind: 'blocked', message: 'Uthyrningen hittades inte.' });
      return;
    }
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user?.uid) return;
      try {
        const snap = await getDoc(doc(db, 'applications', id));
        if (!snap.exists()) {
          setPage({ kind: 'blocked', message: 'Uthyrningen hittades inte.' });
          return;
        }
        const application = snap.data() as Record<string, any>;
        if (application.createdBy !== user.uid && application.ownedBy !== user.uid) {
          setPage({ kind: 'blocked', message: 'Du kan bara byta hyresgäst på din egen uthyrning.' });
          return;
        }
        if (application.status !== 'FINAL_APPROVED') {
          setPage({ kind: 'blocked', message: 'Bara en godkänd, pågående uthyrning kan få en ny hyresgäst.' });
          return;
        }
        const approved = approvedRentalPeriod(application);
        const check = validateTenantChangePeriod({
          approved,
          newStart: searchParams.get('start'),
          newEnd: searchParams.get('end'),
        });
        if (!check.ok) {
          setPage({ kind: 'blocked', message: check.message });
          return;
        }
        const period = { start: check.start, end: check.end };
        setValues(emptyValues(application, period));
        setPage({ kind: 'ready', period, approvedEnd: approved.end as string, application });
      } catch (error) {
        console.error('Kunde inte läsa uthyrningen:', error);
        setPage({ kind: 'blocked', message: 'Uthyrningen kunde inte läsas. Försök igen.' });
      }
    });
    return () => unsubscribe();
  }, [id, searchParams]);

  const handleChange = (name: string) => (value: any) => {
    let val = value?.target?.value !== undefined ? value.target.value : value;
    if (name === 'tenantPersonalNumber') {
      val = formatPersonalNumber(val, values.tenantPersonalNumber);
    }
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async () => {
    if (page.kind !== 'ready' || !id) return;
    setSubmitError(null);

    if (triggerStep2Validation && !triggerStep2Validation()) {
      setTimeout(() => {
        const firstError = document.querySelector('[data-error="true"], .Mui-error');
        if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    const contract = validateContractWithinPeriod({
      periodStart: page.period.start,
      periodEnd: page.period.end,
      contractStart: values.contractStartDate ? dayjs(values.contractStartDate).format('YYYY-MM-DD') : undefined,
      contractEnd: values.contractEndDate ? dayjs(values.contractEndDate).format('YYYY-MM-DD') : undefined,
    });
    if (!contract.ok) {
      setSubmitError(contract.message);
      return;
    }

    const tenantInfo = {
      tenantEmail: values.tenantEmail,
      tenantPersonalNumber: values.tenantPersonalNumber,
      tenantFirstName: values.tenantFirstName,
      tenantLastName: values.tenantLastName,
      tenantPhoneNumber: values.tenantPhoneNumber,
      squareMeter: values.squareMeter,
      rooms: values.rooms,
      contractStartDate: contract.start,
      contractEndDate: contract.end,
      rentalFee: values.rentalFee,
      deposit: values.deposit,
      clearingnummer: values.clearingnummer,
      bankAccountNumber: values.bankAccountNumber,
      bankgiro: values.bankgiro,
      heatingIncluded: values.heatingIncluded || false,
      hotWaterIncluded: values.hotWaterIncluded || false,
      electricityIncluded: values.electricityIncluded || false,
      internetIncluded: values.internetIncluded || false,
      parkingIncluded: values.parkingIncluded || false,
      furnitureIncluded: values.furnitureIncluded || false,
      miscellaneousNotes: values.miscellaneousNotes || '',
    };

    try {
      await changeTenant(id, page.period.start, page.period.end, tenantInfo);
      setSent(true);
    } catch (error: any) {
      setSubmitError(error?.message || 'Hyresgästbytet kunde inte genomföras. Försök igen.');
    }
  };

  if (page.kind === 'loading') {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: '#626262' }}>Hämtar uthyrningen…</Typography>
      </Box>
    );
  }

  if (page.kind === 'blocked') {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', py: 6, px: 2 }}>
        <h1 className="text-2xl font-bold text-[#222222] font-['Poppins'] mb-3">Byt hyresgäst</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-800">{page.message}</p>
        </div>
        <Link to="/applications" className="text-sm font-semibold text-[#62B8E9] hover:underline">
          ← Tillbaka till mina ansökningar
        </Link>
      </Box>
    );
  }

  const { period, approvedEnd, application } = page;

  return (
    <>
      <ApplicationSentModal
        open={sent}
        onClose={() => navigate('/applications')}
        subtitle="application_sent_modal_text_2"
      />

      <Box sx={{ maxWidth: 700, mx: 'auto', px: { xs: 2, sm: 3, md: 0 } }}>
        <h1 className="text-2xl font-bold text-[#222222] font-['Poppins'] mb-1">Byt hyresgäst</h1>
        <p className="text-sm text-[#626262] mb-4">
          {application.address}
          {application.apartmentNumber ? `, lgh ${application.apartmentNumber}` : ''}
        </p>

        {/* Perioden är låst här — den valdes i förra steget och kontrollerades
            mot den godkända uthyrningen. Vill ägaren ändra går hen tillbaka. */}
        <div className="bg-[#f0f9ff] border border-[#bae6fd] rounded-lg p-4 mb-6" data-testid="change-tenant-period">
          <p className="text-sm font-semibold text-[#0369a1]">
            Ny hyresgäst {formatDateSv(period.start)} – {formatDateSv(period.end)}
          </p>
          <p className="text-xs text-[#0369a1] mt-1">
            Uthyrningen är godkänd till och med {formatDateSv(approvedEnd)}.{' '}
            <Link to="/applications" className="underline">Ändra period</Link>
          </p>
        </div>

        <CreateStep2
          values={values as any}
          handleChange={handleChange as any}
          onValidationChange={(hasErrors, triggerValidation) => {
            setStep2HasErrors(hasErrors);
            if (triggerValidation) setTriggerStep2Validation(() => triggerValidation);
          }}
          rentalPeriodStart={period.start}
          rentalPeriodEnd={period.end}
        />

        <Box sx={{ mt: 3, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {submitError && (
            <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}
          <Button
            backgroundColor="#62B8E9"
            textcolor={COLORS.white}
            bordercolor="#62B8E9"
            onClick={handleSubmit}
            disabled={isLoading || step2HasErrors}
            isLoading={isLoading}
          >
            {t('register')}
          </Button>
          {step2HasErrors && (
            <Typography sx={{ color: '#dc2626', fontSize: '14px', textAlign: 'center' }}>
              Fyll i alla obligatoriska fält korrekt för att fortsätta
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ChangeTenant;
